import type { 
  Medicine, 
  BatchStock, 
  Supplier, 
  Purchase, 
  Dispense,
  StockSummary,
  BatchWithMedicine
} from '../types';

class MockPharmacyDB {
  private storageKey = 'pharmacy_data';

  private data = {
    medicines: [] as Medicine[],
    batchStocks: [] as BatchStock[],
    suppliers: [] as Supplier[],
    purchases: [] as Purchase[],
    dispenses: [] as Dispense[],
  };

  constructor() {
    this.loadFromStorage();
    this.seedInitialData();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.data = { ...this.data, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load pharmacy data from localStorage:', error);
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.data));
    } catch (error) {
      console.warn('Failed to save pharmacy data to localStorage:', error);
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private seedInitialData() {
    if (this.data.medicines.length === 0) {
      const now = new Date().toISOString();
      const today = new Date().toISOString().split('T')[0];
      
      const medicines: Medicine[] = [
        {
          id: 'med-1',
          name: 'Paracetamol',
          genericName: 'Acetaminophen',
          brandName: 'Crocin',
          strength: '500mg',
          form: 'tablet',
          route: 'oral',
          manufacturer: 'GSK',
          reorderLevel: 100,
          status: 'active',
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'med-2',
          name: 'Amoxicillin',
          genericName: 'Amoxicillin',
          brandName: 'Amoxil',
          strength: '250mg',
          form: 'capsule',
          route: 'oral',
          manufacturer: 'Pfizer',
          reorderLevel: 50,
          status: 'active',
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'med-3',
          name: 'Insulin',
          genericName: 'Human Insulin',
          brandName: 'Humulin',
          strength: '100IU/ml',
          form: 'injection',
          route: 'iv',
          manufacturer: 'Eli Lilly',
          reorderLevel: 20,
          status: 'active',
          createdAt: now,
          updatedAt: now,
        },
      ];

      const suppliers: Supplier[] = [
        {
          id: 'sup-1',
          name: 'MedSupply Co.',
          contactPerson: 'John Smith',
          phone: '+1-555-0123',
          email: 'john@medsupply.com',
          address: '123 Medical St, Healthcare City',
          taxId: 'TAX123456',
          status: 'active',
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'sup-2',
          name: 'PharmaDist Ltd.',
          contactPerson: 'Sarah Johnson',
          phone: '+1-555-0456',
          email: 'sarah@pharmadist.com',
          address: '456 Pharma Ave, Medicine Town',
          status: 'active',
          createdAt: now,
          updatedAt: now,
        },
      ];

      const batchStocks: BatchStock[] = [
        {
          id: 'batch-1',
          medicineId: 'med-1',
          batchNo: 'PCM001',
          expiryDate: '2025-12-31',
          quantityOnHand: 150,
          location: 'main',
          unitPrice: 2.50,
          mrp: 3.00,
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'batch-2',
          medicineId: 'med-1',
          batchNo: 'PCM002',
          expiryDate: '2025-10-15',
          quantityOnHand: 75,
          location: 'pharmacy',
          unitPrice: 2.50,
          mrp: 3.00,
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'batch-3',
          medicineId: 'med-2',
          batchNo: 'AMX001',
          expiryDate: '2025-11-30',
          quantityOnHand: 30,
          location: 'main',
          unitPrice: 5.00,
          mrp: 6.50,
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'batch-4',
          medicineId: 'med-3',
          batchNo: 'INS001',
          expiryDate: '2025-09-30',
          quantityOnHand: 15,
          location: 'pharmacy',
          unitPrice: 25.00,
          mrp: 30.00,
          createdAt: now,
          updatedAt: now,
        },
      ];

      const dispenses: Dispense[] = [
        {
          id: 'disp-1',
          patientId: 'patient-1',
          prescriptionId: 'rx-1',
          dateTime: `${today}T10:30:00.000Z`,
          items: [
            {
              medicineId: 'med-1',
              batchNo: 'PCM001',
              qty: 10,
              unitPrice: 2.50,
            },
          ],
          total: 25.00,
          paymentStatus: 'paid',
        },
        {
          id: 'disp-2',
          patientId: 'patient-2',
          prescriptionId: 'rx-2',
          dateTime: `${today}T14:15:00.000Z`,
          items: [
            {
              medicineId: 'med-2',
              batchNo: 'AMX001',
              qty: 5,
              unitPrice: 5.00,
            },
          ],
          total: 25.00,
          paymentStatus: 'paid',
        },
      ];

      this.data.medicines = medicines;
      this.data.suppliers = suppliers;
      this.data.batchStocks = batchStocks;
      this.data.dispenses = dispenses;
      this.saveToStorage();
    }
  }

  getMedicines(): Medicine[] {
    return [...this.data.medicines];
  }

  getMedicine(id: string): Medicine | undefined {
    return this.data.medicines.find(m => m.id === id);
  }

  createMedicine(data: Omit<Medicine, 'id' | 'createdAt' | 'updatedAt'>): Medicine {
    const now = new Date().toISOString();
    const medicine: Medicine = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
    };
    this.data.medicines.push(medicine);
    this.saveToStorage();
    return medicine;
  }

  updateMedicine(id: string, data: Partial<Omit<Medicine, 'id' | 'createdAt'>>): Medicine | null {
    const index = this.data.medicines.findIndex(m => m.id === id);
    if (index === -1) return null;

    this.data.medicines[index] = {
      ...this.data.medicines[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.medicines[index];
  }

  searchMedicines(query: string): Medicine[] {
    const lowerQuery = query.toLowerCase();
    return this.data.medicines.filter(m =>
      m.name.toLowerCase().includes(lowerQuery) ||
      m.genericName?.toLowerCase().includes(lowerQuery) ||
      m.brandName?.toLowerCase().includes(lowerQuery)
    );
  }

  getBatchStocks(): BatchStock[] {
    return [...this.data.batchStocks];
  }

  getBatchStock(id: string): BatchStock | undefined {
    return this.data.batchStocks.find(b => b.id === id);
  }

  getBatchStocksByMedicine(medicineId: string): BatchStock[] {
    return this.data.batchStocks.filter(b => b.medicineId === medicineId);
  }

  upsertBatchStock(data: Omit<BatchStock, 'id' | 'createdAt' | 'updatedAt'>): BatchStock {
    const existing = this.data.batchStocks.find(
      b => b.medicineId === data.medicineId && b.batchNo === data.batchNo
    );

    const now = new Date().toISOString();

    if (existing) {
      existing.quantityOnHand += data.quantityOnHand;
      existing.unitPrice = data.unitPrice;
      existing.mrp = data.mrp;
      existing.location = data.location;
      existing.updatedAt = now;
      this.saveToStorage();
      return existing;
    } else {
      const batch: BatchStock = {
        ...data,
        id: this.generateId(),
        createdAt: now,
        updatedAt: now,
      };
      this.data.batchStocks.push(batch);
      this.saveToStorage();
      return batch;
    }
  }

  deductStockForDispense(medicineId: string, batchNo: string, quantity: number): boolean {
    const batch = this.data.batchStocks.find(
      b => b.medicineId === medicineId && b.batchNo === batchNo
    );

    if (!batch || batch.quantityOnHand < quantity) {
      return false; // Insufficient stock
    }

    batch.quantityOnHand -= quantity;
    batch.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return true;
  }

  getStockSummary(): StockSummary[] {
    const summary: StockSummary[] = [];
    const medicineMap = new Map<string, Medicine>();
    
    this.data.medicines.forEach(m => medicineMap.set(m.id, m));

    const medicineStocks = new Map<string, BatchStock[]>();
    this.data.batchStocks.forEach(batch => {
      if (!medicineStocks.has(batch.medicineId)) {
        medicineStocks.set(batch.medicineId, []);
      }
      medicineStocks.get(batch.medicineId)!.push(batch);
    });

    medicineStocks.forEach((batches, medicineId) => {
      const medicine = medicineMap.get(medicineId);
      if (!medicine) return;

      const totalStock = batches.reduce((sum, batch) => sum + batch.quantityOnHand, 0);
      const nearestExpiry = batches
        .filter(b => b.quantityOnHand > 0)
        .sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime())[0]?.expiryDate || '';

      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      summary.push({
        medicineId,
        medicineName: medicine.name,
        totalStock,
        nearestExpiry,
        reorderLevel: medicine.reorderLevel,
        isLowStock: totalStock <= medicine.reorderLevel,
        isNearExpiry: nearestExpiry ? new Date(nearestExpiry) <= thirtyDaysFromNow : false,
      });
    });

    return summary;
  }

  getBatchesWithMedicine(): BatchWithMedicine[] {
    const medicineMap = new Map<string, Medicine>();
    this.data.medicines.forEach(m => medicineMap.set(m.id, m));

    return this.data.batchStocks.map(batch => ({
      ...batch,
      medicine: medicineMap.get(batch.medicineId)!,
    })).filter(b => b.medicine);
  }

  getSuppliers(): Supplier[] {
    return [...this.data.suppliers];
  }

  getSupplier(id: string): Supplier | undefined {
    return this.data.suppliers.find(s => s.id === id);
  }

  createSupplier(data: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>): Supplier {
    const now = new Date().toISOString();
    const supplier: Supplier = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
    };
    this.data.suppliers.push(supplier);
    this.saveToStorage();
    return supplier;
  }

  updateSupplier(id: string, data: Partial<Omit<Supplier, 'id' | 'createdAt'>>): Supplier | null {
    const index = this.data.suppliers.findIndex(s => s.id === id);
    if (index === -1) return null;

    this.data.suppliers[index] = {
      ...this.data.suppliers[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.suppliers[index];
  }

  getPurchases(): Purchase[] {
    return [...this.data.purchases];
  }

  getPurchase(id: string): Purchase | undefined {
    return this.data.purchases.find(p => p.id === id);
  }

  createPurchase(data: Omit<Purchase, 'id'>): Purchase {
    const purchase: Purchase = {
      ...data,
      id: this.generateId(),
    };

    data.items.forEach(item => {
      this.upsertBatchStock({
        medicineId: item.medicineId,
        batchNo: item.batchNo,
        expiryDate: item.expiryDate,
        quantityOnHand: item.qty,
        unitPrice: item.unitPrice,
        mrp: item.mrp,
        location: 'main',
      });
    });

    this.data.purchases.push(purchase);
    this.saveToStorage();
    return purchase;
  }

  getDispenses(): Dispense[] {
    return [...this.data.dispenses];
  }

  getDispense(id: string): Dispense | undefined {
    return this.data.dispenses.find(d => d.id === id);
  }

  createDispense(data: Omit<Dispense, 'id' | 'dateTime'>): Dispense {
    for (const item of data.items) {
      const batch = this.data.batchStocks.find(
        b => b.medicineId === item.medicineId && b.batchNo === item.batchNo
      );
      if (!batch || batch.quantityOnHand < item.qty) {
        throw new Error(`Insufficient stock for ${item.medicineId} batch ${item.batchNo}`);
      }
    }

    const dispense: Dispense = {
      ...data,
      id: this.generateId(),
      dateTime: new Date().toISOString(),
    };

    data.items.forEach(item => {
      this.deductStockForDispense(item.medicineId, item.batchNo, item.qty);
    });

    this.data.dispenses.push(dispense);
    this.saveToStorage();
    return dispense;
  }
}

export const mockPharmacyDB = new MockPharmacyDB();
